import {useEffect, useMemo, useRef} from 'react';
import {NetworkStatus} from '@apollo/client';

import {PolicyFilter, StoredFilterType} from 'modules/decisionsNext/api/types';
import {PolicyPageFilter} from 'filters/types';

import {DEFAULT_FILTER_NAME} from 'core/constants/policyFilter';
import {useFormatGlobalFilter} from 'features/GlobalFilterProvider/hooks/useFormatGlobalFilter';
import {useCypressFilterOverride} from 'filters/hooks/useCypressFilterOverride';
import {dayjs} from 'utils/dateUtils';
import {
  RecentPolicyFiltersDocument,
  useRecentPolicyFiltersQuery,
} from 'features/GlobalFilterProvider/api/policyFilters.generated';
import {PolicyFilterTag} from 'constants/policyFilterTags';
import {useUpdateFilterLastUsedMutation} from 'api/filters/filterMutations.generated';
import {useUpsertPolicyFilterMutation} from 'features/GlobalFilterProvider/api/policyFilterMutations.generated';

import {initialFilter} from 'modules/decisionsNext/components/BaseFilter/utils/filter';

export const createUsePolicyFilterSource =
  ({
    tag,
    useFormatter,
    defaultFilter,
  }: {
    tag: PolicyFilterTag;
    useFormatter: () => (filter: PolicyFilter) => PolicyFilter;
    defaultFilter: PolicyFilter;
  }) =>
  (skip: boolean) => {
    const initCalledRef = useRef(false);
    const updateLastUsedCalledRef = useRef(false);

    const {data, networkStatus} = useRecentPolicyFiltersQuery({
      variables: {
        tag,
      },
      skip,
      notifyOnNetworkStatusChange: true,
    });
    const [updateLastUsed, {loading: updateLastUsedLoading}] = useUpdateFilterLastUsedMutation();
    const [upsertPolicyFilter, {loading}] = useUpsertPolicyFilterMutation();
    const formatFilter = useFormatter();

    const recent = data?.storedFiltersV2.policyFiltersList;

    // Create default filter if none exist
    useEffect(() => {
      if (skip || networkStatus !== NetworkStatus.ready || recent?.length) {
        return;
      }

      if (initCalledRef.current) {
        return;
      }

      initCalledRef.current = true;
      void upsertPolicyFilter({
        variables: {
          input: {
            tag,
            name: DEFAULT_FILTER_NAME,
            type: StoredFilterType.Saved,
            filter: formatFilter(defaultFilter),
          },
        },
        refetchQueries: [
          {
            query: RecentPolicyFiltersDocument,
            variables: {
              tag,
            },
          },
        ],
      });
    }, [recent?.length, networkStatus, skip, loading, formatFilter, upsertPolicyFilter]);

    // Update last used on most recent filter if it's hasn't been used today
    useEffect(() => {
      if (skip || updateLastUsedLoading || !recent?.length) {
        return;
      }

      if (updateLastUsedCalledRef.current) {
        return;
      }

      if (dayjs(recent[0].lastUsed).isBefore(dayjs(), 'day') && recent[0].type === StoredFilterType.Recent) {
        updateLastUsedCalledRef.current = true;
        void updateLastUsed({
          variables: {
            id: recent[0].id,
          },
          refetchQueries: [
            {
              query: RecentPolicyFiltersDocument,
              variables: {
                tag,
              },
            },
          ],
        });
      }
    }, [recent, skip, updateLastUsed, updateLastUsedLoading]);

    return {
      loading: !recent?.length,
      filter: useCypressFilterOverride(
        useMemo<PolicyPageFilter | undefined>(() => {
          if (!recent?.length) {
            return;
          }

          return {
            ...formatFilter(recent[0].filter),
            storedId: recent[0].id,
            _type: 'policy',
          };
        }, [formatFilter, recent])
      ),
    };
  };

export const useGlobalFilterSource = createUsePolicyFilterSource({
  tag: PolicyFilterTag.GLOBAL,
  useFormatter: useFormatGlobalFilter,
  defaultFilter: initialFilter,
});
